import { AssetSummary } from '@/types/crypto';
import { formatUSD, formatPrice, formatPercent } from '@/lib/format';
import { Sparkline } from '@/components/Sparkline';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TrendingUp, TrendingDown, Layers } from 'lucide-react';

interface MarketTableProps {
  assets: AssetSummary[];
  onSelectAsset: (asset: AssetSummary) => void;
}

export const MarketTable = ({ assets, onSelectAsset }: MarketTableProps) => {
  return (
    <div className="rounded-xl border border-border/50 bg-card/30 backdrop-blur-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-border/50 hover:bg-transparent">
            <TableHead className="w-12 text-xs font-medium text-muted-foreground">#</TableHead>
            <TableHead className="text-xs font-medium text-muted-foreground">Ativo</TableHead>
            <TableHead className="text-right text-xs font-medium text-muted-foreground">Preço</TableHead>
            <TableHead className="text-right text-xs font-medium text-muted-foreground">24h</TableHead>
            <TableHead className="text-right text-xs font-medium text-muted-foreground hidden md:table-cell">7d</TableHead>
            <TableHead className="text-right text-xs font-medium text-muted-foreground hidden lg:table-cell">Market Cap</TableHead>
            <TableHead className="text-right text-xs font-medium text-muted-foreground hidden lg:table-cell">Volume 24h</TableHead>
            <TableHead className="text-right text-xs font-medium text-muted-foreground hidden xl:table-cell">TVL</TableHead>
            <TableHead className="w-24 text-right text-xs font-medium text-muted-foreground hidden md:table-cell">7d Chart</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {assets.map((asset, index) => (
            <TableRow
              key={asset.id}
              className="border-border/30 cursor-pointer transition-colors hover:bg-accent/5 group"
              onClick={() => onSelectAsset(asset)}
            >
              {/* Rank */}
              <TableCell className="font-medium text-muted-foreground text-sm">
                {asset.rank || index + 1}
              </TableCell>

              {/* Asset Name & Symbol */}
              <TableCell>
                <div className="flex items-center gap-3">
                  {asset.image ? (
                    <img
                      src={asset.image}
                      alt={asset.name}
                      className="w-8 h-8 rounded-full"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary">
                        {asset.symbol.slice(0, 2)}
                      </span>
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate max-w-[120px] lg:max-w-[180px] group-hover:text-primary transition-colors">
                      {asset.name}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground font-medium">
                        {asset.symbol}
                      </span>
                      {asset.tvl && asset.tvl > 0 && (
                        <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 bg-primary/10 text-primary border-0">
                          <Layers className="h-2.5 w-2.5 mr-0.5" />
                          DeFi
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              </TableCell>

              {/* Price */}
              <TableCell className="text-right font-semibold text-sm">
                {formatPrice(asset.price)}
              </TableCell>

              {/* 24h Change */}
              <TableCell className="text-right">
                <div className={`inline-flex items-center gap-1 text-sm font-medium ${
                  asset.change_24h >= 0 ? 'text-profit' : 'text-loss'
                }`}>
                  {asset.change_24h >= 0 ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {formatPercent(asset.change_24h)}
                </div>
              </TableCell>

              {/* 7d Change */}
              <TableCell className="text-right hidden md:table-cell">
                <span className={`text-sm font-medium ${
                  (asset.change_7d || 0) >= 0 ? 'text-profit' : 'text-loss'
                }`}>
                  {formatPercent(asset.change_7d)}
                </span>
              </TableCell>

              {/* Market Cap */}
              <TableCell className="text-right text-sm text-muted-foreground hidden lg:table-cell">
                {formatUSD(asset.market_cap)}
              </TableCell>

              {/* Volume 24h */}
              <TableCell className="text-right text-sm text-muted-foreground hidden lg:table-cell">
                {formatUSD(asset.volume_24h)}
              </TableCell>

              {/* TVL */}
              <TableCell className="text-right text-sm hidden xl:table-cell">
                {asset.tvl && asset.tvl > 0 ? (
                  <span className="text-primary font-medium">{formatUSD(asset.tvl)}</span>
                ) : (
                  <span className="text-muted-foreground/50">—</span>
                )}
              </TableCell>

              {/* Sparkline */}
              <TableCell className="text-right hidden md:table-cell">
                {asset.sparkline_7d && asset.sparkline_7d.length > 0 ? (
                  <div className="w-20 h-8 ml-auto">
                    <Sparkline data={asset.sparkline_7d} height={32} />
                  </div>
                ) : (
                  <span className="text-muted-foreground/50">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
